import type {GroupSingleOption, OptionGroups, OptionTab} from '@pb/kit-component-modal-options';
import type {SearchTemplateColumn, SortRowData} from 'src/modules/advanced-search-result/typedef';

export const SELECTION_KEY = 'users-groups-search-result';
export const SORTING_WARNING = 'The maximum number of suggestions allowed for sorting is 5000. Please clarify' +
    ' your criteria';
export const SORTING_ERROR = 'The number of suggestions exceeds the maximum allowed. Please clarify your criteria';

export const COLUMNS: Record<string, string> = {
    ACCOUNT_ID: 'accountId',
    USER_ID: 'id',
    GROUP_ID: 'groupId',
    EMAIL: 'email',
    FIRST_NAME: 'firstName',
    LAST_NAME: 'lastName',
    TYPE: 'type',
    STATUS: 'status',
    COUNTRY: 'countryCode',
    STATE: 'stateCode',
    PROVINCE: 'provinceCode',
    DELETED: 'isDeleted',
    CITY: 'city',
    JOB_TITLE: 'jobTitle',
    ADDRESS_1: 'address1',
    ADDRESS_2: 'address2',
    WORK_PHONE: 'workPhone',
    MOBILE_PHONE: 'mobilePhone',
    POSTAL_CODE: 'postalCode',
    FIRST_LICENSE_DATE: 'firstLicenseDate',
    NEWSLETTERS: 'newsletters',
    LCD_SUBSCRIPTION_NEWS: 'lcdSubscriptionNews',
    LCD_SUBSCRIPTION_RESEARCH: 'lcdSubscriptionResearch',
    LAST_PLATFORM_ACTIVITY: 'lastPlatformActivity',
    LAST_MOBILE_ACTIVITY: 'lastMobileActivity',
    SAVED_SEARCHES: 'savedSearches',
    TEMPLATE_USER: 'isTemplateUser',
    ACCOUNT_TEAM_MEMBER: 'isAccountTeamMember',
    CREATOR: 'creator',
    CREATED_DATE: 'createdDate',
    LAST_MODIFIER: 'lastModifier',
    LAST_MODIFIED_DATE: 'lastModifiedDate',
    LAST_MODIFICATION_REASON: 'lastModificationReason',
    SALESFORCE_ID: 'salesforceId',
    MS_DYNAMICS_ID: 'msDynamicsId',
    DEAL_CLOUD_ID: 'dealCloudId',
    HUBSPOT_ID: 'hubspotId',
    VIEW_TOTAL: 'profilesViewsDailyTotal',
    M_VIEW_TOTAL: 'profilesViewsMonthlyTotal',
    PUBDD_TOTAL: 'publicDataDownloadsDailyTotal',
    PUBDM_TOTAL: 'publicDataDownloadsMonthlyTotal',
    PADD_TOTAL: 'patentsDownloadsDailyTotal',
    PMD_TOTAL: 'patentsDownloadsMonthlyTotal',
    CTDD_TOTAL: 'clinicalTrialsDownloadsDailyTotal',
    CTMD_TOTAL: 'clinicalTrialsDownloadsMonthlyTotal',
    DD_TOTAL: 'downloadsDailyTotal',
    DM_TOTAL: 'downloadsMonthlyTotal',
    PDD_TOTAL: 'peopleDownloadsDailyTotal',
    PDM_TOTAL: 'peopleDownloadsMonthlyTotal',
    IC_DD_TOTAL: 'indexComponentsDownloadDailyTotal',
    IC_MD_TOTAL: 'indexComponentsDownloadMonthlyTotal',
    BDCPU_DD_TOTAL: 'bdcPortfolioAndUniverseDailyTotal',
    BDCPU_MD_TOTAL: 'bdcPortfolioAndUniverseMonthlyTotal',
    CLOPHU_MD_TOTAL: 'cloPortfolioHoldingsUniverseMonthlyTotal',
    CLOTDU_MD_TOTAL: 'cloTradeDataUniverseMonthlyTotal',
    MS_TOTAL: 'searchesMonthlyTotal',
    IL_MD_TOTAL: 'indexLevelsDownloadMonthlyTotal',
    VIEW_USED: 'profilesViewsDailyUsed',
    M_VIEW_USED: 'profilesViewsMonthlyUsed',
    PUBDD_USED: 'publicDataDownloadsDailyUsed',
    PUBDM_USED: 'publicDataDownloadsMonthlyUsed',
    PADD_USED: 'patentsDownloadsDailyUsed',
    PMD_USED: 'patentsDownloadsMonthlyUsed',
    CTDD_USED: 'clinicalTrialsDownloadsDailyUsed',
    CTMD_USED: 'clinicalTrialsDownloadsMonthlyUsed',
    DD_USED: 'downloadsDailyUsed',
    DM_USED: 'downloadsMonthlyUsed',
    PDD_USED: 'peopleDownloadsDailyUsed',
    PDM_USED: 'peopleDownloadsMonthlyUsed',
    IC_DD_USED: 'indexComponentsDownloadDailyUsed',
    IC_MD_USED: 'indexComponentsDownloadMonthlyUsed',
    BDCPU_DD_USED: 'bdcPortfolioAndUniverseDailyUsed',
    BDCPU_MD_USED: 'bdcPortfolioAndUniverseMonthlyUsed',
    CLOPHU_MD_USED: 'cloPortfolioHoldingsUniverseMonthlyUsed',
    CLOTDU_MD_USED: 'cloTradeDataUniverseMonthlyUsed',
    MS_USED: 'searchesMonthlyUsed',
    IL_MD_USED: 'indexLevelsDownloadMonthlyUsed',
};

export const LIMITED_SORT_COLUMNS = [
    COLUMNS.ACCOUNT_ID,
    COLUMNS.GROUP_ID,
    COLUMNS.NEWSLETTERS,
    COLUMNS.LCD_SUBSCRIPTION_NEWS,
    COLUMNS.LCD_SUBSCRIPTION_RESEARCH,
    COLUMNS.SAVED_SEARCHES,
    COLUMNS.CREATOR,
    COLUMNS.CREATED_DATE,
    COLUMNS.LAST_MODIFIER,
    COLUMNS.LAST_MODIFIED_DATE,
    COLUMNS.LAST_MODIFICATION_REASON,
    COLUMNS.SALESFORCE_ID,
    COLUMNS.MS_DYNAMICS_ID,
    COLUMNS.DEAL_CLOUD_ID,
    COLUMNS.HUBSPOT_ID,
];

export const TOTAL_LIMIT_COLUMNS = [
    COLUMNS.VIEW_TOTAL,
    COLUMNS.M_VIEW_TOTAL,
    COLUMNS.PUBDD_TOTAL,
    COLUMNS.PUBDM_TOTAL,
    COLUMNS.PADD_TOTAL,
    COLUMNS.PMD_TOTAL,
    COLUMNS.CTDD_TOTAL,
    COLUMNS.CTMD_TOTAL,
    COLUMNS.DD_TOTAL,
    COLUMNS.DM_TOTAL,
    COLUMNS.PDD_TOTAL,
    COLUMNS.PDM_TOTAL,
    COLUMNS.IC_DD_TOTAL,
    COLUMNS.IC_MD_TOTAL,
    COLUMNS.BDCPU_DD_TOTAL,
    COLUMNS.BDCPU_MD_TOTAL,
    COLUMNS.CLOPHU_MD_TOTAL,
    COLUMNS.CLOTDU_MD_TOTAL,
    COLUMNS.MS_TOTAL,
    COLUMNS.IL_MD_TOTAL,
];

export const USED_LIMIT_COLUMNS = [
    COLUMNS.VIEW_USED,
    COLUMNS.M_VIEW_USED,
    COLUMNS.PUBDD_USED,
    COLUMNS.PUBDM_USED,
    COLUMNS.PADD_USED,
    COLUMNS.PMD_USED,
    COLUMNS.CTDD_USED,
    COLUMNS.CTMD_USED,
    COLUMNS.DD_USED,
    COLUMNS.DM_USED,
    COLUMNS.PDD_USED,
    COLUMNS.PDM_USED,
    COLUMNS.IC_DD_USED,
    COLUMNS.IC_MD_USED,
    COLUMNS.BDCPU_DD_USED,
    COLUMNS.BDCPU_MD_USED,
    COLUMNS.CLOPHU_MD_USED,
    COLUMNS.CLOTDU_MD_USED,
    COLUMNS.MS_USED,
    COLUMNS.IL_MD_USED,
];

export const NO_SORT_COLUMNS = [
    ...TOTAL_LIMIT_COLUMNS,
    ...USED_LIMIT_COLUMNS,
];

const NUMBER_SORTING_LABELS: SortRowData = {
    ascLabel: 'Ascending',
    descLabel: 'Descending',
};

const TEXT_SORTING_LABELS: SortRowData = {
    ascLabel: 'A-Z',
    descLabel: 'Z-A',
};

const BOOLEAN_SORTING_LABELS: SortRowData = {
    ascLabel: 'No first',
    descLabel: 'Yes first',
};

const DATE_SORTING_LABELS: SortRowData = {
    ascLabel: 'Oldest to Newest',
    descLabel: 'Newest to Oldest',
};

export const SORING_LABElS: Record<string, SortRowData> = {
    [COLUMNS.ACCOUNT_ID]: TEXT_SORTING_LABELS,
    [COLUMNS.USER_ID]: NUMBER_SORTING_LABELS,
    [COLUMNS.GROUP_ID]: TEXT_SORTING_LABELS,
    [COLUMNS.EMAIL]: TEXT_SORTING_LABELS,
    [COLUMNS.FIRST_NAME]: TEXT_SORTING_LABELS,
    [COLUMNS.LAST_NAME]: TEXT_SORTING_LABELS,
    [COLUMNS.TYPE]: TEXT_SORTING_LABELS,
    [COLUMNS.STATUS]: TEXT_SORTING_LABELS,
    [COLUMNS.COUNTRY]: TEXT_SORTING_LABELS,
    [COLUMNS.STATE]: TEXT_SORTING_LABELS,
    [COLUMNS.PROVINCE]: TEXT_SORTING_LABELS,
    [COLUMNS.DELETED]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.CITY]: TEXT_SORTING_LABELS,
    [COLUMNS.JOB_TITLE]: TEXT_SORTING_LABELS,
    [COLUMNS.ADDRESS_1]: TEXT_SORTING_LABELS,
    [COLUMNS.ADDRESS_2]: TEXT_SORTING_LABELS,
    [COLUMNS.WORK_PHONE]: TEXT_SORTING_LABELS,
    [COLUMNS.MOBILE_PHONE]: TEXT_SORTING_LABELS,
    [COLUMNS.POSTAL_CODE]: TEXT_SORTING_LABELS,
    [COLUMNS.FIRST_LICENSE_DATE]: DATE_SORTING_LABELS,
    [COLUMNS.NEWSLETTERS]: TEXT_SORTING_LABELS,
    [COLUMNS.LAST_PLATFORM_ACTIVITY]: DATE_SORTING_LABELS,
    [COLUMNS.LAST_MOBILE_ACTIVITY]: DATE_SORTING_LABELS,
    [COLUMNS.SAVED_SEARCHES]: NUMBER_SORTING_LABELS,
    [COLUMNS.TEMPLATE_USER]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.ACCOUNT_TEAM_MEMBER]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.CREATOR]: TEXT_SORTING_LABELS,
    [COLUMNS.CREATED_DATE]: DATE_SORTING_LABELS,
    [COLUMNS.LAST_MODIFIER]: TEXT_SORTING_LABELS,
    [COLUMNS.LAST_MODIFIED_DATE]: DATE_SORTING_LABELS,
    [COLUMNS.LAST_MODIFICATION_REASON]: TEXT_SORTING_LABELS,
    [COLUMNS.SALESFORCE_ID]: TEXT_SORTING_LABELS,
    [COLUMNS.MS_DYNAMICS_ID]: TEXT_SORTING_LABELS,
    [COLUMNS.DEAL_CLOUD_ID]: TEXT_SORTING_LABELS,
    [COLUMNS.HUBSPOT_ID]: TEXT_SORTING_LABELS,
};

export const USER_SEARCH_COLUMNS: SearchTemplateColumn[] = [
    {code: COLUMNS.EMAIL, name: 'Email', required: true},
    {code: COLUMNS.USER_ID, name: 'ID', required: false},
    {code: COLUMNS.ACCOUNT_ID, name: 'Primary Account', required: false},
    {code: COLUMNS.GROUP_ID, name: 'Group', required: false},
    {code: COLUMNS.FIRST_NAME, name: 'First Name', required: false},
    {code: COLUMNS.LAST_NAME, name: 'Last Name', required: false},
    {code: COLUMNS.TYPE, name: 'Type', required: false},
    {code: COLUMNS.STATUS, name: 'Status', required: false},
    {code: COLUMNS.COUNTRY, name: 'Country', required: false},
    {code: COLUMNS.STATE, name: 'State', required: false},
    {code: COLUMNS.PROVINCE, name: 'Province', required: false},
    {code: COLUMNS.CITY, name: 'City', required: false},
    {code: COLUMNS.DELETED, name: 'Deleted', required: false},
    {code: COLUMNS.JOB_TITLE, name: 'Job Title', required: false},
    {code: COLUMNS.ADDRESS_1, name: 'Address 1', required: false},
    {code: COLUMNS.ADDRESS_2, name: 'Address 2', required: false},
    {code: COLUMNS.WORK_PHONE, name: 'Work Phone', required: false},
    {code: COLUMNS.MOBILE_PHONE, name: 'Mobile Phone', required: false},
    {code: COLUMNS.POSTAL_CODE, name: 'Postal Code', required: false},
    {code: COLUMNS.FIRST_LICENSE_DATE, name: 'First Licensed', required: false},
    {code: COLUMNS.NEWSLETTERS, name: 'Newsletters', required: false},
    {code: COLUMNS.LCD_SUBSCRIPTION_NEWS, name: 'LCD Subscription News', required: false},
    {code: COLUMNS.LCD_SUBSCRIPTION_RESEARCH, name: 'LCD Subscription Research', required: false},
    {code: COLUMNS.LAST_PLATFORM_ACTIVITY, name: 'Last Platform Active', required: false},
    {code: COLUMNS.LAST_MOBILE_ACTIVITY, name: 'Last Mobile Active', required: false},
    {code: COLUMNS.SAVED_SEARCHES, name: 'Saved Searches', required: false},
    {code: COLUMNS.TEMPLATE_USER, name: 'Template User', required: false},
    {code: COLUMNS.ACCOUNT_TEAM_MEMBER, name: 'Account Team Member', required: false},
    {code: COLUMNS.CREATOR, name: 'Creator', required: false},
    {code: COLUMNS.CREATED_DATE, name: 'Created Date', required: false},
    {code: COLUMNS.LAST_MODIFIER, name: 'Last Modifier', required: false},
    {code: COLUMNS.LAST_MODIFIED_DATE, name: 'Last Modified Date', required: false},
    {code: COLUMNS.LAST_MODIFICATION_REASON, name: 'Last Modification Reason', required: false},
    {code: COLUMNS.SALESFORCE_ID, name: 'Salesforce ID', required: false},
    {code: COLUMNS.MS_DYNAMICS_ID, name: 'MS Dynamics ID', required: false},
    {code: COLUMNS.DEAL_CLOUD_ID, name: 'DealCloud ID', required: false},
    {code: COLUMNS.HUBSPOT_ID, name: 'HubSpot ID', required: false},
    {code: COLUMNS.VIEW_TOTAL, name: 'Profiles Views Daily Total', required: false},
    {code: COLUMNS.M_VIEW_TOTAL, name: 'Profiles Views Monthly Total', required: false},
    {code: COLUMNS.PUBDD_TOTAL, name: 'Public Data Downloads Daily Total', required: false},
    {code: COLUMNS.PUBDM_TOTAL, name: 'Public Data Downloads Monthly Total', required: false},
    {code: COLUMNS.PADD_TOTAL, name: 'Patents Downloads Daily Total', required: false},
    {code: COLUMNS.PMD_TOTAL, name: 'Patents Downloads Monthly Total', required: false},
    {code: COLUMNS.CTDD_TOTAL, name: 'Clinical Trials Downloads Daily Total', required: false},
    {code: COLUMNS.CTMD_TOTAL, name: 'Clinical Trials Downloads Monthly Total', required: false},
    {code: COLUMNS.DD_TOTAL, name: 'Downloads Daily Total', required: false},
    {code: COLUMNS.DM_TOTAL, name: 'Downloads Monthly Total', required: false},
    {code: COLUMNS.PDD_TOTAL, name: 'People Downloads Daily Total', required: false},
    {code: COLUMNS.PDM_TOTAL, name: 'People Downloads Monthly Total', required: false},
    {code: COLUMNS.IC_DD_TOTAL, name: 'Index Components Download Daily Total', required: false},
    {code: COLUMNS.IC_MD_TOTAL, name: 'Index Components Download Monthly Total', required: false},
    {code: COLUMNS.BDCPU_DD_TOTAL, name: 'BDC Portfolio And Universe Daily Total', required: false},
    {code: COLUMNS.BDCPU_MD_TOTAL, name: 'BDC Portfolio And Universe Monthly Total', required: false},
    {code: COLUMNS.CLOPHU_MD_TOTAL, name: 'CLO Portfolio Holdings Universe Monthly Total', required: false},
    {code: COLUMNS.CLOTDU_MD_TOTAL, name: 'CLO Trade Data Universe Download Monthly Total', required: false},
    {code: COLUMNS.MS_TOTAL, name: 'Searches Monthly Total', required: false},
    {code: COLUMNS.IL_MD_TOTAL, name: 'Index Levels Download Monthly Total', required: false},
    {code: COLUMNS.VIEW_USED, name: 'Profiles Views Daily Used', required: false},
    {code: COLUMNS.M_VIEW_USED, name: 'Profiles Views Monthly Used', required: false},
    {code: COLUMNS.PUBDD_USED, name: 'Public Data Downloads Daily Used', required: false},
    {code: COLUMNS.PUBDM_USED, name: 'Public Data Downloads Monthly Used', required: false},
    {code: COLUMNS.PADD_USED, name: 'Patents Downloads Daily Used', required: false},
    {code: COLUMNS.PMD_USED, name: 'Patents Downloads Monthly Used', required: false},
    {code: COLUMNS.CTDD_USED, name: 'Clinical Trials Downloads Daily Used', required: false},
    {code: COLUMNS.CTMD_USED, name: 'Clinical Trials Downloads Monthly Used', required: false},
    {code: COLUMNS.DD_USED, name: 'Downloads Daily Used', required: false},
    {code: COLUMNS.DM_USED, name: 'Downloads Monthly Used', required: false},
    {code: COLUMNS.PDD_USED, name: 'People Downloads Daily Used', required: false},
    {code: COLUMNS.PDM_USED, name: 'People Downloads Monthly Used', required: false},
    {code: COLUMNS.IC_DD_USED, name: 'Index Components Download Daily Used', required: false},
    {code: COLUMNS.IC_MD_USED, name: 'Index Components Download Monthly Used', required: false},
    {code: COLUMNS.BDCPU_DD_USED, name: 'BDC Portfolio And Universe Daily Used', required: false},
    {code: COLUMNS.BDCPU_MD_USED, name: 'BDC Portfolio And Universe Monthly Used', required: false},
    {code: COLUMNS.CLOPHU_MD_USED, name: 'CLO Portfolio Holdings Universe Monthly Used', required: false},
    {code: COLUMNS.CLOTDU_MD_USED, name: 'CLO Trade Data Universe Download Monthly Used', required: false},
    {code: COLUMNS.MS_USED, name: 'Searches Monthly Used', required: false},
    {code: COLUMNS.IL_MD_USED, name: 'Index Levels Download Monthly Used', required: false},
];


export enum UserActivitySource {
    PLATFORM = 'PLATFORM',
    MOBILE = 'MOBILE',
}
export const LOADING_PRIORITY = [
    {chunk: 'audit', columnId: COLUMNS.CREATOR, priority: 1},
    {chunk: 'audit', columnId: COLUMNS.CREATED_DATE, priority: 2},
    {chunk: 'audit', columnId: COLUMNS.LAST_MODIFIER, priority: 3},
    {chunk: 'audit', columnId: COLUMNS.LAST_MODIFIED_DATE, priority: 4},
    {chunk: 'audit', columnId: COLUMNS.LAST_MODIFICATION_REASON, priority: 5},

    {chunk: 'userActivity', columnId: COLUMNS.LAST_PLATFORM_ACTIVITY, priority: 1},
    {chunk: 'userActivity', columnId: COLUMNS.LAST_MOBILE_ACTIVITY, priority: 2},

    {chunk: 'crm', columnId: COLUMNS.SALESFORCE_ID, priority: 1},
    {chunk: 'crm', columnId: COLUMNS.MS_DYNAMICS_ID, priority: 2},
    {chunk: 'crm', columnId: COLUMNS.DEAL_CLOUD_ID, priority: 3},
    {chunk: 'crm', columnId: COLUMNS.HUBSPOT_ID, priority: 4},

    {chunk: 'limits', columnId: COLUMNS.VIEW_TOTAL, priority: 1},
    {chunk: 'limits', columnId: COLUMNS.M_VIEW_TOTAL, priority: 2},
    {chunk: 'limits', columnId: COLUMNS.PUBDD_TOTAL, priority: 3},
    {chunk: 'limits', columnId: COLUMNS.PUBDM_TOTAL, priority: 4},
    {chunk: 'limits', columnId: COLUMNS.PADD_TOTAL, priority: 5},
    {chunk: 'limits', columnId: COLUMNS.PMD_TOTAL, priority: 6},
    {chunk: 'limits', columnId: COLUMNS.CTDD_TOTAL, priority: 7},
    {chunk: 'limits', columnId: COLUMNS.CTMD_TOTAL, priority: 8},
    {chunk: 'limits', columnId: COLUMNS.DD_TOTAL, priority: 9},
    {chunk: 'limits', columnId: COLUMNS.DM_TOTAL, priority: 10},
    {chunk: 'limits', columnId: COLUMNS.PDD_TOTAL, priority: 11},
    {chunk: 'limits', columnId: COLUMNS.PDM_TOTAL, priority: 12},
    {chunk: 'limits', columnId: COLUMNS.IC_DD_TOTAL, priority: 13},
    {chunk: 'limits', columnId: COLUMNS.IC_MD_TOTAL, priority: 14},
    {chunk: 'limits', columnId: COLUMNS.BDCPU_DD_TOTAL, priority: 15},
    {chunk: 'limits', columnId: COLUMNS.BDCPU_MD_TOTAL, priority: 16},
    {chunk: 'limits', columnId: COLUMNS.MS_TOTAL, priority: 17},
    {chunk: 'limits', columnId: COLUMNS.IL_MD_TOTAL, priority: 18},
    {chunk: 'limits', columnId: COLUMNS.VIEW_USED, priority: 19},
    {chunk: 'limits', columnId: COLUMNS.M_VIEW_USED, priority: 20},
    {chunk: 'limits', columnId: COLUMNS.PUBDD_USED, priority: 21},
    {chunk: 'limits', columnId: COLUMNS.PUBDM_USED, priority: 22},
    {chunk: 'limits', columnId: COLUMNS.PADD_USED, priority: 23},
    {chunk: 'limits', columnId: COLUMNS.PMD_USED, priority: 24},
    {chunk: 'limits', columnId: COLUMNS.CTDD_USED, priority: 25},
    {chunk: 'limits', columnId: COLUMNS.CTMD_USED, priority: 26},
    {chunk: 'limits', columnId: COLUMNS.DD_USED, priority: 27},
    {chunk: 'limits', columnId: COLUMNS.DM_USED, priority: 28},
    {chunk: 'limits', columnId: COLUMNS.PDD_USED, priority: 29},
    {chunk: 'limits', columnId: COLUMNS.PDM_USED, priority: 30},
    {chunk: 'limits', columnId: COLUMNS.IC_DD_USED, priority: 31},
    {chunk: 'limits', columnId: COLUMNS.IC_MD_USED, priority: 32},
    {chunk: 'limits', columnId: COLUMNS.BDCPU_DD_USED, priority: 33},
    {chunk: 'limits', columnId: COLUMNS.BDCPU_MD_USED, priority: 34},
    {chunk: 'limits', columnId: COLUMNS.CLOPHU_MD_USED, priority: 35},
    {chunk: 'limits', columnId: COLUMNS.CLOPHU_MD_TOTAL, priority: 36},
    {chunk: 'limits', columnId: COLUMNS.CLOTDU_MD_USED, priority: 37},
    {chunk: 'limits', columnId: COLUMNS.CLOTDU_MD_TOTAL, priority: 38},
    {chunk: 'limits', columnId: COLUMNS.MS_USED, priority: 39},
    {chunk: 'limits', columnId: COLUMNS.IL_MD_USED, priority: 40},
];

export const OPTIONS_SETTINGS = {
    count_1: {
        size: 'Sizes.size-12',
        width: 's',
    },
    count_2: {
        size: 'Sizes.size-6',
        width: 's',
    },
    count_3: {
        size: 'Sizes.size-4',
        width: 'm',
    },
    count_4: {
        size: 'Sizes.size-3',
        width: 'm',
    },
};

export const OPTION_GROUPS: OptionGroups = {
    USER_SEARCH_COLUMNS: [
        {options: ['userDetails', 'userContactInformation', 'roleSettings', 'crm', 'userSettings', 'userActivity', 'audit']},
        {options: ['userAccountInformation', 'limits']},
    ],
};


export const OPTIONS_MAP: Record<string, GroupSingleOption> = {
    [COLUMNS.EMAIL]: {
        hidden: false,
        defaultOption: true,
        option: {
            label: 'Email',
            fixed: true,
            pinned: false,
            optionId: 'email',
        },
    },
    [COLUMNS.USER_ID]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'ID',
            fixed: false,
            pinned: false,
            optionId: 'id',
        },
    },
    [COLUMNS.ACCOUNT_ID]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Primary Account',
            fixed: false,
            pinned: false,
            optionId: 'accountId',
        },
    },
    [COLUMNS.GROUP_ID]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Group',
            fixed: false,
            pinned: false,
            optionId: 'groupId',
        },
    },
    [COLUMNS.FIRST_NAME]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'First Name',
            fixed: false,
            pinned: false,
            optionId: 'firstName',
        },
    },
    [COLUMNS.LAST_NAME]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Last Name',
            fixed: false,
            pinned: false,
            optionId: 'lastName',
        },
    },
    [COLUMNS.TYPE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Type',
            fixed: false,
            pinned: false,
            optionId: 'type',
        },
    },
    [COLUMNS.STATUS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Status',
            fixed: false,
            pinned: false,
            optionId: 'status',
        },
    },
    [COLUMNS.COUNTRY]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Country',
            fixed: false,
            pinned: false,
            optionId: 'countryCode',
        },
    },
    [COLUMNS.CITY]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'City',
            fixed: false,
            pinned: false,
            optionId: 'city',
        },
    },
    [COLUMNS.DELETED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Deleted',
            fixed: false,
            pinned: false,
            optionId: 'isDeleted',
        },
    },
    [COLUMNS.JOB_TITLE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Job Title',
            fixed: false,
            pinned: false,
            optionId: 'jobTitle',
        },
    },
    [COLUMNS.WORK_PHONE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Work Phone',
            fixed: false,
            pinned: false,
            optionId: 'workPhone',
        },
    },
    [COLUMNS.MOBILE_PHONE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Mobile Phone',
            fixed: false,
            pinned: false,
            optionId: 'mobilePhone',
        },
    },
    [COLUMNS.FIRST_LICENSE_DATE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'First Licensed',
            fixed: false,
            pinned: false,
            optionId: 'firstLicenseDate',
        },
    },
    [COLUMNS.NEWSLETTERS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Newsletters',
            fixed: false,
            pinned: false,
            optionId: 'newsletters',
        },
    },
    [COLUMNS.LCD_SUBSCRIPTION_NEWS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'LCD Subscription News',
            fixed: false,
            pinned: false,
            optionId: 'lcdSubscriptionNews',
        },
    },
    [COLUMNS.LCD_SUBSCRIPTION_RESEARCH]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'LCD Subscription Research',
            fixed: false,
            pinned: false,
            optionId: 'lcdSubscriptionResearch',
        },
    },
    [COLUMNS.LAST_PLATFORM_ACTIVITY]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Last Platform Active',
            fixed: false,
            pinned: false,
            optionId: 'lastPlatformActivity',
        },
    },
    [COLUMNS.LAST_MOBILE_ACTIVITY]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Last Mobile Active',
            fixed: false,
            pinned: false,
            optionId: 'lastMobileActivity',
        },
    },
    [COLUMNS.TEMPLATE_USER]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Template User',
            fixed: false,
            pinned: false,
            optionId: 'isTemplateUser',
        },
    },
    [COLUMNS.ACCOUNT_TEAM_MEMBER]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Account Team Member',
            fixed: false,
            pinned: false,
            optionId: 'isAccountTeamMember',
        },
    },
    [COLUMNS.ADDRESS_1]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Address 1',
            fixed: false,
            pinned: false,
            optionId: 'address1',
        },
    },
    [COLUMNS.ADDRESS_2]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Address 2',
            fixed: false,
            pinned: false,
            optionId: 'address2',
        },
    },
    [COLUMNS.STATE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'State',
            fixed: false,
            pinned: false,
            optionId: 'stateCode',
        },
    },
    [COLUMNS.PROVINCE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Province',
            fixed: false,
            pinned: false,
            optionId: 'provinceCode',
        },
    },
    [COLUMNS.POSTAL_CODE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Postal Code',
            fixed: false,
            pinned: false,
            optionId: 'postalCode',
        },
    },
    [COLUMNS.SAVED_SEARCHES]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Saved Searches',
            fixed: false,
            pinned: false,
            optionId: 'savedSearches',
        },
    },
    [COLUMNS.CREATOR]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Creator',
            fixed: false,
            pinned: false,
            optionId: 'creator',
        },
    },
    [COLUMNS.CREATED_DATE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Created Date',
            fixed: false,
            pinned: false,
            optionId: 'createdDate',
        },
    },
    [COLUMNS.LAST_MODIFIER]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Last Modifier',
            fixed: false,
            pinned: false,
            optionId: 'lastModifier',
        },
    },
    [COLUMNS.LAST_MODIFIED_DATE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Last Modified Date',
            fixed: false,
            pinned: false,
            optionId: 'lastModifiedDate',
        },
    },
    [COLUMNS.LAST_MODIFICATION_REASON]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Last Modification Reason',
            fixed: false,
            pinned: false,
            optionId: 'lastModificationReason',
        },
    },
    [COLUMNS.SALESFORCE_ID]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Salesforce ID',
            fixed: false,
            pinned: false,
            optionId: 'salesforceId',
        },
    },
    [COLUMNS.MS_DYNAMICS_ID]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'MS Dynamics ID',
            fixed: false,
            pinned: false,
            optionId: 'msDynamicsId',
        },
    },
    [COLUMNS.DEAL_CLOUD_ID]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'DealCloud ID',
            fixed: false,
            pinned: false,
            optionId: 'dealCloudId',
        },
    },
    [COLUMNS.HUBSPOT_ID]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'HubSpot ID',
            fixed: false,
            pinned: false,
            optionId: 'hubspotId',
        },
    },
    [COLUMNS.VIEW_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Profiles Views Daily Total',
            fixed: false,
            pinned: false,
            optionId: 'profilesViewsDailyTotal',
        },
    },
    [COLUMNS.M_VIEW_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Profiles Views Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'profilesViewsMonthlyTotal',
        },
    },
    [COLUMNS.PUBDD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Public Data Downloads Daily Total',
            fixed: false,
            pinned: false,
            optionId: 'publicDataDownloadsDailyTotal',
        },
    },
    [COLUMNS.PUBDM_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Public Data Downloads Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'publicDataDownloadsMonthlyTotal',
        },
    },
    [COLUMNS.PADD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Patents Downloads Daily Total',
            fixed: false,
            pinned: false,
            optionId: 'patentsDownloadsDailyTotal',
        },
    },
    [COLUMNS.PMD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Patents Downloads Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'patentsDownloadsMonthlyTotal',
        },
    },
    [COLUMNS.CTDD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Clinical Trials Downloads Daily Total',
            fixed: false,
            pinned: false,
            optionId: 'clinicalTrialsDownloadsDailyTotal',
        },
    },
    [COLUMNS.CTMD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Clinical Trials Downloads Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'clinicalTrialsDownloadsMonthlyTotal',
        },
    },
    [COLUMNS.DD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Downloads Daily Total',
            fixed: false,
            pinned: false,
            optionId: 'downloadsDailyTotal',
        },
    },
    [COLUMNS.DM_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Downloads Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'downloadsMonthlyTotal',
        },
    },
    [COLUMNS.PDD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'People Downloads Daily Total',
            fixed: false,
            pinned: false,
            optionId: 'peopleDownloadsDailyTotal',
        },
    },
    [COLUMNS.PDM_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'People Downloads Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'peopleDownloadsMonthlyTotal',
        },
    },
    [COLUMNS.IC_DD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Index Components Download Daily Total',
            fixed: false,
            pinned: false,
            optionId: 'indexComponentsDownloadDailyTotal',
        },
    },
    [COLUMNS.IC_MD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Index Components Download Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'indexComponentsDownloadMonthlyTotal',
        },
    },
    [COLUMNS.BDCPU_DD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'BDC Portfolio And Universe Daily Total',
            fixed: false,
            pinned: false,
            optionId: 'bdcPortfolioAndUniverseDailyTotal',
        },
    },
    [COLUMNS.BDCPU_MD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'BDC Portfolio And Universe Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'bdcPortfolioAndUniverseMonthlyTotal',
        },
    },
    [COLUMNS.CLOPHU_MD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'CLO Portfolio Holdings Universe Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'cloPortfolioHoldingsUniverseMonthlyTotal',
        },
    },
    [COLUMNS.CLOTDU_MD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'CLO Trade Data Universe Download Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'cloTradeDataUniverseMonthlyTotal',
        },
    },
    [COLUMNS.MS_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Searches Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'searchesMonthlyTotal',
        },
    },
    [COLUMNS.IL_MD_TOTAL]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Index Levels Download Monthly Total',
            fixed: false,
            pinned: false,
            optionId: 'indexLevelsDownloadMonthlyTotal',
        },
    },
    [COLUMNS.VIEW_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Profiles Views Daily Used',
            fixed: false,
            pinned: false,
            optionId: 'profilesViewsDailyUsed',
        },
    },
    [COLUMNS.M_VIEW_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Profiles Views Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'profilesViewsMonthlyUsed',
        },
    },
    [COLUMNS.PUBDD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Public Data Downloads Daily Used',
            fixed: false,
            pinned: false,
            optionId: 'publicDataDownloadsDailyUsed',
        },
    },
    [COLUMNS.PUBDM_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Public Data Downloads Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'publicDataDownloadsMonthlyUsed',
        },
    },
    [COLUMNS.PADD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Patents Downloads Daily Used',
            fixed: false,
            pinned: false,
            optionId: 'patentsDownloadsDailyUsed',
        },
    },
    [COLUMNS.PMD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Patents Downloads Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'patentsDownloadsMonthlyUsed',
        },
    },
    [COLUMNS.CTDD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Clinical Trials Downloads Daily Used',
            fixed: false,
            pinned: false,
            optionId: 'clinicalTrialsDownloadsDailyUsed',
        },
    },
    [COLUMNS.CTMD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Clinical Trials Downloads Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'clinicalTrialsDownloadsMonthlyUsed',
        },
    },
    [COLUMNS.DD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Downloads Daily Used',
            fixed: false,
            pinned: false,
            optionId: 'downloadsDailyUsed',
        },
    },
    [COLUMNS.DM_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Downloads Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'downloadsMonthlyUsed',
        },
    },
    [COLUMNS.PDD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'People Downloads Daily Used',
            fixed: false,
            pinned: false,
            optionId: 'peopleDownloadsDailyUsed',
        },
    },
    [COLUMNS.PDM_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'People Downloads Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'peopleDownloadsMonthlyUsed',
        },
    },
    [COLUMNS.IC_DD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Index Components Download Daily Used',
            fixed: false,
            pinned: false,
            optionId: 'indexComponentsDownloadDailyUsed',
        },
    },
    [COLUMNS.IC_MD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Index Components Download Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'indexComponentsDownloadMonthlyUsed',
        },
    },
    [COLUMNS.BDCPU_DD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'BDC Portfolio And Universe Daily Used',
            fixed: false,
            pinned: false,
            optionId: 'bdcPortfolioAndUniverseDailyUsed',
        },
    },
    [COLUMNS.BDCPU_MD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'BDC Portfolio And Universe Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'bdcPortfolioAndUniverseMonthlyUsed',
        },
    },
    [COLUMNS.CLOPHU_MD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'CLO Portfolio Holdings Universe Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'cloPortfolioHoldingsUniverseMonthlyUsed',
        },
    },
    [COLUMNS.CLOTDU_MD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'CLO Trade Data Universe Download Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'cloTradeDataUniverseMonthlyUsed',
        },
    },
    [COLUMNS.MS_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Searches Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'searchesMonthlyUsed',
        },
    },
    [COLUMNS.IL_MD_USED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Index Levels Download Monthly Used',
            fixed: false,
            pinned: false,
            optionId: 'indexLevelsDownloadMonthlyUsed',
        },
    },
};

export const ALL_OPTIONS: GroupSingleOption[] = Object.values(OPTIONS_MAP);

export const ITEMS: OptionTab[] = [
    {
        tabId: 'USER_SEARCH_COLUMNS',
        caption: 'User Search Columns',
        items: [
            {
                id: 'userDetails',
                caption: 'User Details',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.EMAIL],
                    OPTIONS_MAP[COLUMNS.USER_ID],
                    OPTIONS_MAP[COLUMNS.FIRST_NAME],
                    OPTIONS_MAP[COLUMNS.LAST_NAME],
                    OPTIONS_MAP[COLUMNS.JOB_TITLE],
                ],
            },
            {
                id: 'userAccountInformation',
                caption: 'User Account Information',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.ACCOUNT_ID],
                    OPTIONS_MAP[COLUMNS.GROUP_ID],
                    OPTIONS_MAP[COLUMNS.TYPE],
                    OPTIONS_MAP[COLUMNS.STATUS],
                    OPTIONS_MAP[COLUMNS.DELETED],
                    OPTIONS_MAP[COLUMNS.TEMPLATE_USER],
                    OPTIONS_MAP[COLUMNS.FIRST_LICENSE_DATE],
                ],
            },
            {
                id: 'userContactInformation',
                caption: 'User Contact Information',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.COUNTRY],
                    OPTIONS_MAP[COLUMNS.STATE],
                    OPTIONS_MAP[COLUMNS.PROVINCE],
                    OPTIONS_MAP[COLUMNS.CITY],
                    OPTIONS_MAP[COLUMNS.ADDRESS_1],
                    OPTIONS_MAP[COLUMNS.ADDRESS_2],
                    OPTIONS_MAP[COLUMNS.WORK_PHONE],
                    OPTIONS_MAP[COLUMNS.MOBILE_PHONE],
                    OPTIONS_MAP[COLUMNS.POSTAL_CODE],
                ],
            },
            {
                id: 'userSettings',
                caption: 'User Settings',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.NEWSLETTERS],
                    OPTIONS_MAP[COLUMNS.LCD_SUBSCRIPTION_NEWS],
                    OPTIONS_MAP[COLUMNS.LCD_SUBSCRIPTION_RESEARCH],
                    OPTIONS_MAP[COLUMNS.SAVED_SEARCHES],
                ],
            },
            {
                id: 'userActivity',
                caption: 'User Activity',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.LAST_PLATFORM_ACTIVITY],
                    OPTIONS_MAP[COLUMNS.LAST_MOBILE_ACTIVITY],
                ],
            },
            {
                id: 'roleSettings',
                caption: 'Role Settings',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.ACCOUNT_TEAM_MEMBER],
                ],
            },
            {
                id: 'audit',
                caption: 'Audit',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.CREATOR],
                    OPTIONS_MAP[COLUMNS.CREATED_DATE],
                    OPTIONS_MAP[COLUMNS.LAST_MODIFIER],
                    OPTIONS_MAP[COLUMNS.LAST_MODIFIED_DATE],
                    OPTIONS_MAP[COLUMNS.LAST_MODIFICATION_REASON],
                ],
            },
            {
                id: 'crm',
                caption: 'CRM Settings',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.SALESFORCE_ID],
                    OPTIONS_MAP[COLUMNS.MS_DYNAMICS_ID],
                    OPTIONS_MAP[COLUMNS.DEAL_CLOUD_ID],
                    OPTIONS_MAP[COLUMNS.HUBSPOT_ID],
                ],
            },
            {
                id: 'limits',
                caption: 'User Limits',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.VIEW_USED],
                    OPTIONS_MAP[COLUMNS.VIEW_TOTAL],
                    OPTIONS_MAP[COLUMNS.M_VIEW_USED],
                    OPTIONS_MAP[COLUMNS.M_VIEW_TOTAL],
                    OPTIONS_MAP[COLUMNS.PUBDD_USED],
                    OPTIONS_MAP[COLUMNS.PUBDD_TOTAL],
                    OPTIONS_MAP[COLUMNS.PUBDM_USED],
                    OPTIONS_MAP[COLUMNS.PUBDM_TOTAL],
                    OPTIONS_MAP[COLUMNS.PADD_USED],
                    OPTIONS_MAP[COLUMNS.PADD_TOTAL],
                    OPTIONS_MAP[COLUMNS.PMD_USED],
                    OPTIONS_MAP[COLUMNS.PMD_TOTAL],
                    OPTIONS_MAP[COLUMNS.CTDD_USED],
                    OPTIONS_MAP[COLUMNS.CTDD_TOTAL],
                    OPTIONS_MAP[COLUMNS.CTMD_USED],
                    OPTIONS_MAP[COLUMNS.CTMD_TOTAL],
                    OPTIONS_MAP[COLUMNS.DD_USED],
                    OPTIONS_MAP[COLUMNS.DD_TOTAL],
                    OPTIONS_MAP[COLUMNS.DM_USED],
                    OPTIONS_MAP[COLUMNS.DM_TOTAL],
                    OPTIONS_MAP[COLUMNS.PDD_USED],
                    OPTIONS_MAP[COLUMNS.PDD_TOTAL],
                    OPTIONS_MAP[COLUMNS.PDM_USED],
                    OPTIONS_MAP[COLUMNS.PDM_TOTAL],
                    OPTIONS_MAP[COLUMNS.IC_DD_USED],
                    OPTIONS_MAP[COLUMNS.IC_DD_TOTAL],
                    OPTIONS_MAP[COLUMNS.IC_MD_USED],
                    OPTIONS_MAP[COLUMNS.IC_MD_TOTAL],
                    OPTIONS_MAP[COLUMNS.BDCPU_DD_USED],
                    OPTIONS_MAP[COLUMNS.BDCPU_DD_TOTAL],
                    OPTIONS_MAP[COLUMNS.BDCPU_MD_USED],
                    OPTIONS_MAP[COLUMNS.BDCPU_MD_TOTAL],
                    OPTIONS_MAP[COLUMNS.CLOPHU_MD_USED],
                    OPTIONS_MAP[COLUMNS.CLOPHU_MD_TOTAL],
                    OPTIONS_MAP[COLUMNS.CLOTDU_MD_USED],
                    OPTIONS_MAP[COLUMNS.CLOTDU_MD_TOTAL],
                    OPTIONS_MAP[COLUMNS.MS_USED],
                    OPTIONS_MAP[COLUMNS.MS_TOTAL],
                    OPTIONS_MAP[COLUMNS.IL_MD_USED],
                    OPTIONS_MAP[COLUMNS.IL_MD_TOTAL],
                ],
            },
        ],
        hidden: true,
    },
];

export const HEADERS = Object.fromEntries(
    USER_SEARCH_COLUMNS.map(({code, name}) => [code, name]),
);

export const COLUMNS_DATA = [
    {
        columnId: COLUMNS.EMAIL,
        fixed: true,
        defaultWidth: 324,
        minWidth: 324,
        maxWidth: 520,
    },
    {
        columnId: COLUMNS.ID,
        fixed: false,
        defaultWidth: 200,
        minWidth: 100,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.ACCOUNT_ID,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.GROUP_ID,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.FIRST_NAME,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.LAST_NAME,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.TYPE,
        fixed: false,
        defaultWidth: 260,
        minWidth: 260,
        maxWidth: 500,
    },
    {
        columnId: COLUMNS.STATUS,
        fixed: false,
        defaultWidth: 260,
        minWidth: 260,
        maxWidth: 500,
    },
    {
        columnId: COLUMNS.COUNTRY,
        fixed: false,
        defaultWidth: 260,
        minWidth: 260,
        maxWidth: 500,
    },
    {
        columnId: COLUMNS.STATE,
        fixed: false,
        defaultWidth: 260,
        minWidth: 260,
        maxWidth: 500,
    },
    {
        columnId: COLUMNS.PROVINCE,
        fixed: false,
        defaultWidth: 260,
        minWidth: 260,
        maxWidth: 500,
    },
    {
        columnId: COLUMNS.CITY,
        fixed: false,
        defaultWidth: 260,
        minWidth: 260,
        maxWidth: 500,
    },
    {
        columnId: COLUMNS.DELETED,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.JOB_TITLE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.WORK_PHONE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.MOBILE_PHONE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.FIRST_LICENSE_DATE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.NEWSLETTERS,
        fixed: false,
        defaultWidth: 300,
        minWidth: 200,
        maxWidth: 600,
    },
    {
        columnId: COLUMNS.LCD_SUBSCRIPTION_NEWS,
        fixed: false,
        defaultWidth: 300,
        minWidth: 200,
        maxWidth: 600,
    },
    {
        columnId: COLUMNS.LCD_SUBSCRIPTION_RESEARCH,
        fixed: false,
        defaultWidth: 300,
        minWidth: 200,
        maxWidth: 600,
    },
    {
        columnId: COLUMNS.LAST_PLATFORM_ACTIVITY,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.LAST_MOBILE_ACTIVITY,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.TEMPLATE_USER,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.ACCOUNT_TEAM_MEMBER,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.ADDRESS_1,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.ADDRESS_2,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.POSTAL_CODE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.SAVED_SEARCHES,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.CREATOR,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.CREATED_DATE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.LAST_MODIFIER,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.LAST_MODIFIED_DATE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.LAST_MODIFICATION_REASON,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    },
    ...[
        COLUMNS.SALESFORCE_ID,
        COLUMNS.MS_DYNAMICS_ID,
        COLUMNS.DEAL_CLOUD_ID,
        COLUMNS.HUBSPOT_ID,
    ].map(columnId => ({
        columnId,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 400,
    })),
    ...[...TOTAL_LIMIT_COLUMNS, ...USED_LIMIT_COLUMNS].map(columnId => ({
        columnId,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 300,
    })),
];
